import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styles from "./BlogCreate.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
  CustomTextInput,
} from "../../components";
import PopupModal from "../../components/PopupModal/PopupModal";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../services/Api";

/* ============================================================
   QUILL CONFIG
============================================================ */
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }, { size: ["small", false, "large", "huge"] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "size",
  "align",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "link",
  "image",
  "video",
];

/* ============================================================
   STATUS OPTIONS (Active = 1, Inactive = 0)
============================================================ */
const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];
const DEFAULT_STATUS_OPTION = STATUS_OPTIONS[0]; // Active

/* ============================================================
   INITIAL STATE
============================================================ */
const initialFormState = {
  title: "",
  tagline: "",
  author: "",
  designation: "",
  category: null,
  tags: [],
  cover_file: null,
  cover_original_name: null,
  cover_upload_path: "",
  content: "",
  metaTitle: "",
  metaDescription: "",
  status: DEFAULT_STATUS_OPTION, // ✅ default Active
};

/* ============================================================
   HELPERS
============================================================ */
const formatDateTime = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const toFullImageUrl = (path) => {
  if (!path) return "";
  const p = String(path).trim();
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  if (p.startsWith("blob:")) return p;

  const base = IMAGE_BASE_URL.replace(/\/+$/, ""); // "https://dev.ifillip.com/api"
  const origin = (() => {
    try {
      return new URL(base).origin;
    } catch {
      return base;
    }
  })(); // "https://dev.ifillip.com"

  // Stored paths are root-absolute ("/api/uploads/..."), so join to ORIGIN
  // to avoid doubling the "/api" segment.
  if (p.startsWith("/")) return `${origin}${p}`;
  return `${base}/${p}`;
};

/* ============================================================
   VALIDATION
============================================================ */
const EMOJI_REGEX = /\p{Extended_Pictographic}/u;
const containsEmoji = (str = "") => EMOJI_REGEX.test(str);

// Allowed character sets
const TITLE_TEXT_REGEX = /^[a-zA-Z0-9\s.,!?:;'"\-()&/|]+$/;
const NAME_REGEX = /^[a-zA-Z\s.'-]+$/;
const DESIGNATION_REGEX = /^[a-zA-Z0-9\s.,&\-/()]+$/;
const META_DESC_REGEX = /^[a-zA-Z0-9\s.,!?:;'"\-()&/|]+$/;

// Per-field validators — return error message string or "" when valid
export const fieldValidators = {
  title: (value) => {
    if (containsEmoji(value)) return "Emojis are not allowed in title";
    const v = (value || "").trim();
    if (!v) return "Title is required";
    if (!TITLE_TEXT_REGEX.test(v))
      return "Title contains invalid special characters";
    if (v.length < 5) return "Title must be at least 5 characters";
    if (v.length > 200) return "Title must be 200 characters or less";
    return "";
  },
  tagline: (value) => {
    if (containsEmoji(value)) return "Emojis are not allowed in tagline";
    const v = (value || "").trim();
    if (!v) return "Tagline is required";
    if (!TITLE_TEXT_REGEX.test(v))
      return "Tagline contains invalid special characters";
    if (v.length < 5) return "Tagline must be at least 5 characters";
    if (v.length > 300) return "Tagline must be 300 characters or less";
    return "";
  },
  author: (value) => {
    if (containsEmoji(value)) return "Emojis are not allowed in author name";
    const v = (value || "").trim();
    if (!v) return "Author name is required";
    if (!NAME_REGEX.test(v))
      return "Author name can only contain letters, spaces, dots, hyphens, and apostrophes";
    if (v.length < 2) return "Author name must be at least 2 characters";
    if (v.length > 50) return "Author name must be 50 characters or less";
    return "";
  },
  designation: (value) => {
    if (containsEmoji(value)) return "Emojis are not allowed in designation";
    const v = (value || "").trim();
    if (!v) return "Designation is required";
    if (!DESIGNATION_REGEX.test(v))
      return "Designation contains invalid special characters";
    if (v.length < 2) return "Designation must be at least 2 characters";
    if (v.length > 100) return "Designation must be 100 characters or less";
    return "";
  },
  metaTitle: (value) => {
    if (containsEmoji(value)) return "Emojis are not allowed in meta title";
    const v = (value || "").trim();
    if (!v) return "Meta Title is required";
    if (!TITLE_TEXT_REGEX.test(v))
      return "Meta Title contains invalid special characters";
    if (v.length < 5) return "Meta Title must be at least 5 characters";
    if (v.length > 100) return "Meta Title must be 100 characters or less";
    return "";
  },
  metaDescription: (value) => {
    if (containsEmoji(value))
      return "Emojis are not allowed in meta description";
    const v = (value || "").trim();
    if (!v) return "Meta Description is required";
    if (!META_DESC_REGEX.test(v))
      return "Meta Description contains invalid special characters";
    if (v.length < 10) return "Meta Description must be at least 10 characters";
    if (v.length > 300)
      return "Meta Description must be 300 characters or less";
    return "";
  },
};

/* ============================================================
   COMPONENT
============================================================ */
const BlogCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const isEditMode = !!id;

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(""); // ✅ NEW: only for preview
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

/* ─── Fetch blog data for edit mode ─── */
/* ─── Fetch blog data for edit mode ─── */
const fetchBlogData = useCallback(async () => {
  if (!id) return;
  setFetchLoading(true);

  try {
    const response = await AxiosInstance.post("/blog/view", {
      id: Number(id),
    });
    const blog = response?.data?.blog;

    if (response?.data?.status && blog) {
      const categoryFromApi = Array.isArray(blog?.category)
        ? blog.category[0]
        : null;
      const categoryValue =
        categoryFromApi?.category_id ||
        blog?.categories_id ||
        blog?.categorie_name ||
        blog?.categories_name ||
        "";

      const categoryLabel =
        categoryFromApi?.category_name ||
        blog?.categorie_name ||
        blog?.categories_name ||
        "";

      let categoryOption = null;
      if (categoryValue || categoryLabel) {
        categoryOption = {
          value: String(categoryValue || categoryLabel),
          label: categoryLabel || String(categoryValue),
          category_id: categoryFromApi?.category_id || categoryValue || "",
          category_name: categoryLabel || "",
          isNew: false,
        };
      }

      const blogTags = (Array.isArray(blog?.tag) ? blog.tag : []).map(
        (tag) => ({
          value: String(tag?.tag_id || tag?.tag_name || ""),
          label: tag?.tag_name || String(tag?.tag_id || ""),
          tag_id: tag?.tag_id || "",
          tag_name: tag?.tag_name || "",
          isNew: false,
        }),
      );

      // ✅ Map blog_status from API to status option
      const blogStatusFromApi = Number(blog?.blog_status);
      const statusOption =
        blogStatusFromApi === 0
          ? STATUS_OPTIONS[1] // Inactive
          : STATUS_OPTIONS[0]; // Active (default)

      setFormData({
        title: blog?.title || "",
        tagline: blog?.tagline || "",
        author: blog?.author_name || "",
        designation: blog?.author_designation || "",
        category: categoryOption,
        tags: blogTags,
        cover_file: null,
        cover_original_name: blog?.cover_image_originalname || null,
        cover_upload_path: blog?.cover_image_path || "",
        content: blog?.content || "",
        metaTitle: blog?.meta_title || "",
        metaDescription: blog?.meta_description || "",
        status: statusOption, // ✅ load existing status
      });

      // ✅ Generate preview URL for existing image (only for preview display)
      const coverPath = blog?.cover_image_path || "";
      if (coverPath) {
        const fullUrl = toFullImageUrl(coverPath);
        setPreviewImageUrl(fullUrl);
      } else {
        setPreviewImageUrl("");
      }
    } else {
      throw new Error(response?.data?.message || "Failed to fetch blog data");
    }
  } catch (error) {
    console.error("Error fetching blog:", error);
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Error fetching blog data";
    setModalState({
      open: true,
      type: "error",
      message: errorMessage,
      redirectPath: "/dashboard/blog",
    });
  } finally {
    setFetchLoading(false);
  }
}, [id]);

  useEffect(() => {
    if (isEditMode) fetchBlogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  /* ─── Fetch dropdown options ─── */
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const [tagRes, categoryRes] = await Promise.all([
          AxiosInstance.get("/blog/taglist"),
          AxiosInstance.get("/blog/categorieslist"),
        ]);

        const tags = Array.isArray(tagRes?.data?.tag)
          ? tagRes.data.tag.map((item) => ({
              value: String(item?.id || ""),
              label: item?.name || "",
              tag_id: item?.id || "",
              tag_name: item?.name || "",
              isNew: false,
            }))
          : [];

        const categories = Array.isArray(categoryRes?.data?.tag)
          ? categoryRes.data.tag.map((item) => ({
              value: String(item?.id || ""),
              label: item?.name || "",
              category_id: item?.id || "",
              category_name: item?.name || "",
              isNew: false,
            }))
          : [];

        setTagOptions(tags);
        setCategoryOptions(categories);
      } catch (err) {
        console.error("Error fetching blog dropdown options:", err);
      }
    };

    fetchDropdownOptions();
  }, []);

  /* ─── Cleanup blob URL ─── */
  useEffect(() => {
    return () => {
      if (
        formData.cover_upload_path &&
        formData.cover_upload_path.startsWith("blob:")
      ) {
        URL.revokeObjectURL(formData.cover_upload_path);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================================================
     REAL-TIME VALIDATION HANDLER
  ============================================================ */
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Run validator immediately for real-time feedback
      const validator = fieldValidators[name];
      if (validator) {
        const error = validator(value);
        setErrors((prev) => ({ ...prev, [name]: error }));
      } else if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [errors],
  );

  /* ─── Dropdown change handler ─── */
  const handleDropdownChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [errors],
  );

  /* ─── Tags change handler ─── */
  const handleTagsChange = useCallback(
    (selectedTags) => {
      setFormData((prev) => ({ ...prev, tags: selectedTags || [] }));
      if (errors.tags) {
        setErrors((prev) => ({ ...prev, tags: "" }));
      }
    },
    [errors],
  );

  /* ─── Quill content change ─── */
  const handleContentChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, content: value }));

    // Real-time validation for content
    const textOnly = (value || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    let contentError = "";
    if (containsEmoji(textOnly)) {
      contentError = "Emojis are not allowed in content";
    } else if (!textOnly) {
      contentError = "Content is required";
    } else if (textOnly.length < 50) {
      contentError = "Content must be at least 50 characters";
    }
    setErrors((prev) => ({ ...prev, content: contentError }));
  }, []);

// After setFormData({ ... }), add this:
  /* ─── Cover image upload ─── */
  const handleFileChange = useCallback(async (file) => {
    if (!file) {
      setFormData((prev) => {
        if (
          prev.cover_upload_path &&
          prev.cover_upload_path.startsWith("blob:")
        ) {
          URL.revokeObjectURL(prev.cover_upload_path);
        }
        return {
          ...prev,
          cover_file: null,
          cover_original_name: null,
          cover_upload_path: "",
        };
      });
      setPreviewImageUrl(""); // ✅ Clear preview URL
      setErrors((prev) => ({ ...prev, coverImage: "" }));
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const MAX_SIZE = 3 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        coverImage: "Only JPG, JPEG, PNG files are allowed",
      }));
      return;
    }
    if (file.size > MAX_SIZE) {
      setErrors((prev) => ({
        ...prev,
        coverImage: "File size must be 3 MB or less",
      }));
      return;
    }

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("blog_image", file);
      setUploadingImage(true);

      const response = await AxiosInstance.post(
        "/blog/upload",
        formDataPayload,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response?.data?.status && response?.data?.data) {
        const uploadedFile = response.data.data;
        setFormData((prev) => ({
          ...prev,
          cover_file: file,
          cover_original_name: uploadedFile.blog_image || file.name,
          cover_upload_path: uploadedFile.blog_image_path || "",
        }));
        setPreviewImageUrl(uploadedFile.blog_image_url || ""); // ✅ Store ONLY for preview
        setErrors((prev) => ({ ...prev, coverImage: "" }));
      } else {
        throw new Error(response?.data?.message || "Invalid upload response");
      }
    } catch (error) {
      console.error("File upload error:", error);
      setErrors((prev) => ({
        ...prev,
        coverImage: "Failed to upload file. Please try again.",
      }));
    } finally {
      setUploadingImage(false);
    }
  }, []);
  /* ============================================================
     FULL FORM VALIDATION (on submit)
  ============================================================ */
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Text fields — use per-field validators
    [
      "title",
      "tagline",
      "author",
      "designation",
      "metaTitle",
      "metaDescription",
    ].forEach((field) => {
      const err = fieldValidators[field](formData[field]);
      if (err) newErrors[field] = err;
    });

    // Category
    if (!formData.category) {
      newErrors.category = "Category is required";
    } else {
      const catName = (
        formData.category.category_name ||
        formData.category.label ||
        ""
      ).trim();
      if (containsEmoji(catName)) {
        newErrors.category = "Emojis are not allowed in category";
      } else if (!catName) {
        newErrors.category = "Category name cannot be empty";
      }
    }

    // Tags
    if (!formData.tags || formData.tags.length === 0) {
      newErrors.tags = "At least one tag is required";
    } else {
      for (const t of formData.tags) {
        const tagName = (t.tag_name || t.label || "").trim();
        if (containsEmoji(tagName)) {
          newErrors.tags = "Emojis are not allowed in tags";
          break;
        }
        if (!tagName) {
          newErrors.tags = "Tag name cannot be empty";
          break;
        }
      }
    }

    // Cover image
    if (!formData.cover_original_name) {
      newErrors.coverImage = "Cover image is required";
    }

    // Content
    const contentText = (formData.content || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    if (containsEmoji(contentText)) {
      newErrors.content = "Emojis are not allowed in content";
    } else if (!contentText) {
      newErrors.content = "Content is required";
    } else if (contentText.length < 50) {
      newErrors.content = "Content must be at least 50 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /* ============================================================
     PAYLOAD PREPARATION
     - For NEW category/tag → send id as ""
     - For EXISTING category/tag → send numeric id
  ============================================================ */
  const preparePayload = useCallback(() => {
    const user = TokenService.getUser();
    const userId = user?.id || user?.user_id || 1;

    // Category id resolution
    const cat = formData.category;
    let categoryId = "";
    if (cat) {
      const isCatNew =
        cat.isNew === true || cat.category_id === "" || cat.category_id == null;
      if (!isCatNew) {
        const parsed = Number(cat.category_id);
        categoryId = Number.isNaN(parsed) ? "" : parsed;
      }
    }

    // Tag list resolution
    const tagPayload = (formData.tags || []).map((t) => {
      const isTagNew = t.isNew === true || t.tag_id === "" || t.tag_id == null;
      let tagId = "";
      if (!isTagNew) {
        const parsed = Number(t.tag_id);
        tagId = Number.isNaN(parsed) ? "" : parsed;
      }
      return {
        tag_id: tagId,
        tag_name: (t.tag_name || t.label || "").trim(),
      };
    });

    // ✅ Resolve blog_status:
    //  - Create mode → always 1 (Active)
    //  - Edit mode   → from selected dropdown (Active=1, Inactive=0)
    const blogStatus = isEditMode ? Number(formData.status?.value ?? 1) : 1;

    return {
      ...(isEditMode ? { id: Number(id) } : {}),
      title: formData.title.trim(),
      tagline: formData.tagline.trim(),
      content: formData.content,
      author_name: formData.author.trim(),
      author_designation: formData.designation.trim(),
      meta_title: formData.metaTitle.trim(),
      meta_description: formData.metaDescription.trim(),
      categories_id: categoryId,
      categories_name: (cat?.category_name || cat?.label || "").trim(),
      tag: tagPayload,
      cover_image_originalname: formData.cover_original_name || "",
      cover_image_path: formData.cover_upload_path || "",
      ...(isEditMode
        ? { updated_by: userId }
        : {
            created_by: userId,
            created_date: formatDateTime(),
          }),
      blog_status: blogStatus,
    };
  }, [formData, id, isEditMode]);

  /* ─── Submit ─── */
  const handleSubmit = useCallback(async () => {
    if (uploadingImage) {
      setErrors((prev) => ({
        ...prev,
        coverImage: "Please wait until image upload completes.",
      }));
      return;
    }
    if (!validateForm()) return;
    setLoading(true);

    try {
      const payload = preparePayload();
      let response;

      if (isEditMode) {
        response = await AxiosInstance.post("/blog/edit", payload);
      } else {
        response = await AxiosInstance.post("/blog/create", payload);
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: isEditMode
            ? "Blog updated successfully!"
            : "Blog created successfully!",
          redirectPath: "/dashboard/blog",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            `Failed to ${isEditMode ? "update" : "create"} blog`,
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} blog:`,
        error,
      );
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        `Error ${isEditMode ? "updating" : "creating"} blog`;
      setModalState({
        open: true,
        type: "error",
        message: errorMessage,
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  }, [uploadingImage, validateForm, preparePayload, isEditMode]);

  const handleCancel = useCallback(
    () => navigate("/dashboard/blog"),
    [navigate],
  );

  const handlePreview = useCallback(() => {
    if (!validateForm()) return;
    setPreviewMode(true);
  }, [validateForm]);

  const closePreview = useCallback(() => setPreviewMode(false), []);

  const handleModalOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) navigate(redirectPath);
  };

  /* ─── Helper to create a new category option ─── */
  const handleCreateCategory = useCallback(
    (inputValue) => {
      const trimmed = (inputValue || "").trim();
      if (!trimmed) return;
      if (containsEmoji(trimmed)) {
        setErrors((prev) => ({
          ...prev,
          category: "Emojis are not allowed in category",
        }));
        return;
      }
      if (!/^[a-zA-Z0-9\s.,&\-/()]+$/.test(trimmed)) {
        setErrors((prev) => ({
          ...prev,
          category: "Category contains invalid special characters",
        }));
        return;
      }

      const newOption = {
        value: `new-cat-${Date.now()}`,
        label: trimmed,
        category_id: "", // EMPTY ID for new categories
        category_name: trimmed,
        isNew: true,
      };
      setCategoryOptions((prev) => [...prev, newOption]);
      handleDropdownChange("category", newOption);
    },
    [handleDropdownChange],
  );

  /* ─── Helper to create a new tag option ─── */
  const handleCreateTag = useCallback((inputValue) => {
    const trimmed = (inputValue || "").trim();
    if (!trimmed) return;
    if (containsEmoji(trimmed)) {
      setErrors((prev) => ({
        ...prev,
        tags: "Emojis are not allowed in tags",
      }));
      return;
    }
    if (!/^[a-zA-Z0-9\s.,&\-/()]+$/.test(trimmed)) {
      setErrors((prev) => ({
        ...prev,
        tags: "Tag contains invalid special characters",
      }));
      return;
    }

    const newTag = {
      value: `new-tag-${Date.now()}`,
      label: trimmed,
      tag_id: "", // EMPTY ID for new tags
      tag_name: trimmed,
      isNew: true,
    };
    setTagOptions((prev) => [...prev, newTag]);
    setFormData((prev) => ({
      ...prev,
      tags: [...(prev.tags || []), newTag],
    }));
    setErrors((prev) => ({ ...prev, tags: "" }));
  }, []);

  /* ─── Loading state ─── */
  if (fetchLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loadingContainer}>
          <i
            className="fa-solid fa-spinner fa-spin"
            style={{ fontSize: "24px" }}
          />
          <span style={{ marginLeft: "10px" }}>Loading blog data...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleModalOk}
      />

      {/* Preview Overlay (unchanged) */}
      {previewMode && (
        <div className={styles.previewOverlay} onClick={closePreview}>
          <div
            className={styles.previewContent}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "1100px",
              padding: 0,
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              className={styles.previewClose}
              onClick={closePreview}
              aria-label="Close preview"
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                zIndex: 20,
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
              }}
            >
              <i className="fa-solid fa-xmark" />
            </button>

            <div
              style={{
                position: "relative",
                minHeight: "420px",
                overflow: "hidden",
              }}
            >
              <img
                src={previewImageUrl || ""} // ✅ Using previewImageUrl state
                alt={formData.title}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.currentTarget.src =
                    "https://lh3.googleusercontent.com/aida-public/AB6AXuC9HQUCpha79m5ojhdmprH7HIa-bp2j9xJONxY55sGxE4tq4jd1iDo8tL1hI__gTulHRcltbIgT_L966JRYjvMLZOugqfgHgJ9BJx_jPjM5QRGRIrVwFAPKuJMtrwow5dWGE3lnwiVWaALPzWmjdxfZKDtzwXnrfxGnurPcJlh4Sguad0GCm-USXmsgI-rEvq_V5Q-NSfDCayqYRUkCZNIB1dCkfrVjr6K1PZO6qQ_WpsOhU2Ep6nfFJtUdhEz-je6dBI2e0FkzyaVm";
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 100%)",
                }}
              />

              <div
                style={{
                  position: "relative",
                  zIndex: 10,
                  maxWidth: "680px",
                  margin: "0 auto",
                  padding: "60px 40px 40px",
                }}
              >
                <div
                  style={{
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    padding: "32px",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    {formData.category && (
                      <span
                        style={{
                          background: "#eef2ff",
                          color: "#4338ca",
                          padding: "6px 16px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {formData.category.label}
                      </span>
                    )}
                  </div>

                  <h1
                    style={{
                      fontSize: "28px",
                      fontWeight: 800,
                      color: "#1a1a2e",
                      lineHeight: 1.3,
                      marginBottom: "8px",
                      background: "linear-gradient(135deg, #1a1a2e, #4338ca)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {formData.title}
                  </h1>

                  <p
                    style={{
                      fontSize: "15px",
                      color: "#64748b",
                      marginBottom: "20px",
                      lineHeight: 1.5,
                    }}
                  >
                    {formData.tagline}
                  </p>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      paddingTop: "16px",
                      borderTop: "1px solid #e2e8f0",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #4338ca, #6366f1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "16px",
                      }}
                    >
                      {formData.author?.charAt(0)?.toUpperCase() || "A"}
                    </div>
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#1a1a2e",
                        }}
                      >
                        {formData.author}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#64748b",
                        }}
                      >
                        {formData.designation}
                      </p>
                    </div>
                    <div
                      style={{
                        width: "1px",
                        height: "30px",
                        background: "#e2e8f0",
                        margin: "0 8px",
                      }}
                    />
                    <div style={{ fontSize: "13px", color: "#64748b" }}>
                      <i
                        className="far fa-calendar-alt"
                        style={{ marginRight: "6px" }}
                      />
                      {new Date().toLocaleDateString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                      })}
                    </div>
                  </div>

                  {formData.tags?.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginTop: "16px",
                      }}
                    >
                      {formData.tags.map((tag) => (
                        <span
                          key={tag.value}
                          style={{
                            background: "#f1f5f9",
                            color: "#475569",
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          # {tag.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              style={{ padding: "40px", maxWidth: "800px", margin: "0 auto" }}
            >
              <article
                style={{
                  fontSize: "16px",
                  lineHeight: 1.8,
                  color: "#334155",
                  wordBreak: "break-word",
                }}
                dangerouslySetInnerHTML={{ __html: formData.content }}
              />

              {(formData.metaTitle || formData.metaDescription) && (
                <div
                  style={{
                    marginTop: "40px",
                    padding: "24px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1a1a2e",
                      marginBottom: "16px",
                    }}
                  >
                    <i
                      className="fa-solid fa-magnifying-glass"
                      style={{ marginRight: "8px" }}
                    />
                    SEO PREVIEW
                  </h3>
                  {formData.metaTitle && (
                    <div style={{ marginBottom: "12px" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        META TITLE
                      </span>
                      <p
                        style={{
                          margin: "4px 0 0",
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#1a56db",
                        }}
                      >
                        {formData.metaTitle}
                      </p>
                    </div>
                  )}
                  {formData.metaDescription && (
                    <div>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        META DESCRIPTION
                      </span>
                      <p
                        style={{
                          margin: "4px 0 0",
                          fontSize: "14px",
                          color: "#475569",
                          lineHeight: 1.5,
                        }}
                      >
                        {formData.metaDescription}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.pageWrapper}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumbHeader}>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbLink}>Home</span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbCurrent}>
              {isEditMode ? "Edit Blog" : "Blog Creation"}
            </span>
          </div>
          <button
            type="button"
            className={styles.viewListButton}
            onClick={handleCancel}
          >
            <i className="fa-solid fa-list" style={{ marginRight: "8px" }} />
            <span>View List</span>
          </button>
        </div>

        {/* Main Form */}
        <div className={styles.container}>
          <div className={styles.section}>
            {/* ✅ Section header: title left, status dropdown right (Edit mode only) */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                marginBottom: "16px",
              }}
            >
              <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
                {isEditMode ? "Edit Blog" : "Create Blog"}
              </h2>

              {isEditMode && (
                <div style={{ minWidth: "220px" }}>
                  <CustomDropdown
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={(value) =>
                      handleDropdownChange(
                        "status",
                        value || DEFAULT_STATUS_OPTION,
                      )
                    }
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                    size="md"
                  />
                </div>
              )}
            </div>

            <div className="row">
              {/* Meta Title */}
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Meta Title"
                  name="metaTitle"
                  value={formData.metaTitle}
                  onChange={handleInputChange}
                  placeholder="Enter SEO Meta Title"
                  required
                  error={errors.metaTitle}
                  size="md"
                />
              </div>

              {/* Meta Description */}
              <div className="col-12 px-2 mt-2">
                <label className={styles.contentLabel}>
                  Meta Description <span className={styles.required}>*</span>
                </label>
                <textarea
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleInputChange}
                  placeholder="Enter SEO Meta Description (used for search engine snippets)"
                  rows={3}
                  className={`${styles.metaTextarea} ${
                    errors.metaDescription ? styles.metaTextareaError : ""
                  }`}
                />
                {errors.metaDescription && (
                  <span className={styles.errorText}>
                    {errors.metaDescription}
                  </span>
                )}
              </div>

              {/* Title + Tagline */}
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter blog Title"
                  required
                  error={errors.title}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Tagline"
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleInputChange}
                  placeholder="Enter blog Tagline"
                  required
                  error={errors.tagline}
                  size="md"
                />
              </div>

              {/* Author + Designation */}
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Enter Author Name"
                  required
                  error={errors.author}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  placeholder="Enter Author Designation"
                  required
                  error={errors.designation}
                  size="md"
                />
              </div>

              {/* Category — Creatable */}
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={(value) => handleDropdownChange("category", value)}
                  options={categoryOptions}
                  placeholder="Select or type to create a new category..."
                  required
                  error={errors.category}
                  size="md"
                  createOptionKey="create"
                  onCreateOption={handleCreateCategory}
                  createOptionLabel={(input) => `+ Create "${input}"`}
                />
              </div>

              {/* Tags — Creatable + Multi */}
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="Tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleTagsChange}
                  options={tagOptions}
                  placeholder="Select or type to create new tags..."
                  required
                  error={errors.tags}
                  size="md"
                  isMulti
                  createOptionKey="create"
                  onCreateOption={handleCreateTag}
                  createOptionLabel={(input) => `+ Create "${input}"`}
                />
              </div>

              {/* Cover Image */}
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomFileInput
                  label="Upload Cover Image (JPG, JPEG, PNG | Max size: 3MB)"
                  name="coverImage"
                  value={formData.cover_original_name || null}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/jpg,image/png"
                  placeholder={formData.cover_original_name || "No file chosen"}
                  buttonText="Choose File"
                  required
                />
                {errors.coverImage && (
                  <span className={styles.errorText}>{errors.coverImage}</span>
                )}
              </div>

              {/* Content */}
              <div className="col-12 px-2 mt-3">
                <label className={styles.contentLabel}>
                  Content <span className={styles.required}>*</span>
                </label>
                <div
                  className={`${styles.quillWrapper} ${errors.content ? styles.quillError : ""}`}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Start writing..."
                  />
                </div>
                {errors.content && (
                  <span className={styles.errorText}>{errors.content}</span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className={styles.formActions}>
            <CustomButton
              variant="outline-dark"
              onClick={handleCancel}
              fullWidth={false}
              className={styles.cancelButton}
            >
              Cancel
            </CustomButton>

            <CustomButton
              variant="primary"
              onClick={handlePreview}
              fullWidth={false}
              className={styles.previewButton}
            >
              <i className="fa-solid fa-eye" style={{ marginRight: "8px" }} />
              Preview
            </CustomButton>

            <CustomButton
              variant="primary"
              onClick={handleSubmit}
              loading={loading || uploadingImage}
              loadingText={isEditMode ? "Updating..." : "Submitting..."}
              fullWidth={false}
              className={styles.submitButton}
            >
              <i
                className={`fa-solid ${isEditMode ? "fa-save" : "fa-paper-plane"}`}
                style={{ marginRight: "8px" }}
              />
              {isEditMode ? "Update" : "Submit"}
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogCreate;
